import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';

import { capitalizeString, interpolate } from '../../../utils/globalHelpers';
import makeSelectMain, {
  getLoanDataDictionary,
  getMainClientInfo,
  getProfileCurrentSetting,
} from '../../Main/selectors';
import { selectForm as selectAutopayForm } from '../../Payments/Autopay/selectors';
import { FETCH_ERROR_PROPS, PAPERLESS_TOGGLE_TEXT_DICT } from './constants';
import {
  getMultiLoanPaperlessDetails,
  getPaperlessFormProps,
  isAlertSettingsMatch,
} from './helpers';
import { initialState } from './reducer';

/**
 * Direct selector to the Communications state domain
 */
const selectCommunicationsDomain = (state) => state.communications || initialState;

/**
 * Direct selector to the intl object
 */
const selectIntl = (_, props) => props.intl;

const selectCommunicationsScreenReaderNotifications = () => createSelector(
  makeSelectCommunications('errors'),
  ({ alerts, paperless }) => {
    const nonFormErrors = [alerts, paperless];
    return nonFormErrors.filter((error) => !!error);
  },
);

const selectCurrentPaymentAlertSetting = () => createSelector(
  makeSelectCommunications('currentAlertSettings'),
  (currentAlertSettings) => currentAlertSettings.find(({ alertId }) => alertId === 4),
);

const selectDesktopSettingsRowsData = () => createSelector(
  selectSettingsPaperlessButtonDetails(),
  (paperlessButtonDetails) => [
    {
      buttonProps: { label: 'go to paperless page', route: '/settings/communications/paperless' },
      details: paperlessButtonDetails,
      label: 'Paperless Billing',
      loading: false,
    },
    {
      buttonProps: {
        label: 'go to text and email notifications page',
        route: '/settings/communications/notifications',
      },
      label: 'Text and Email Notifications',
      loading: false,
    },
  ],
);

const selectError = (prop) => createSelector(
  makeSelectCommunications('errors'),
  (errors) => errors[prop],
);

const selectFetchErrorCopy = () => createSelector(
  getMainClientInfo('customerServicePhoneNumber'),
  (customerServicePhoneNumber) => {
    const { body, title } = FETCH_ERROR_PROPS;
    return { body: interpolate(body, { customerServicePhoneNumber }), title };
  },
);

const selectFormAlertSettings = () => createSelector(
  makeSelectCommunications('alertsProfile'),
  makeSelectCommunications('formAlertSettings'),
  (alertsProfile, formAlertSettings) => {
    const { isSmsAvailable } = alertsProfile;
    const formattedFormAlertSetting = isSmsAvailable ?
      formAlertSettings :
      formAlertSettings.map(({
        isSmsEnabled,
        ...restProps
      }) => ({ isSmsEnabled: false, ...restProps }));
    return formattedFormAlertSetting;
  },
);

const selectFormPaperlessSettings = () => createSelector(
  makeSelectCommunications('formPaperlessSettings'),
  makeSelectCommunications('selectedLoan'),
  (formPaperlessSettings, selectedLoan) => {
    if (formPaperlessSettings[selectedLoan]) {
      return formPaperlessSettings[selectedLoan].status;
    }
    return '';
  },
);

const selectHasMobileNumberOnFile = () => createSelector(
  getProfileCurrentSetting(),
  ({ mobileNumber }) => !!mobileNumber,
);

const selectIsEligibleForPaperlessBilling = () => createSelector(
  makeSelectCommunications('formPaperlessSettings'),
  makeSelectMain('selectedLoan'),
  (formPaperlessSettings, selectedLoan) => {
    if (formPaperlessSettings[selectedLoan]) {
      return formPaperlessSettings[selectedLoan].status === 'Not_Enabled';
    }
    return false;
  },
);

const selectNotificationsFormProps = () => createSelector(
  makeSelectCommunications('alertsProfile'),
  makeSelectCommunications('currentAlertSettings'),
  makeSelectCommunications('formAlertSettings'),
  (alertsProfile, currentAlertSettings, formAlertSettings) => {
    const { isEmailAvailable = false, isSmsAvailable = false } = alertsProfile;
    return {
      alertSettings: formAlertSettings,
      isButtonDisabled: isAlertSettingsMatch(currentAlertSettings, formAlertSettings),
      isLoanEmailAllowed: isEmailAvailable,
      isLoanSmsAllowed: isSmsAvailable,
    };
  },
);

const selectPaperlessFormData = () => createSelector(
  getLoanDataDictionary(),
  makeSelectCommunications('currentPaperlessSettings'),
  makeSelectCommunications('formPaperlessSettings'),
  (loanDataDict, currentPaperlessSettings, formPaperlessSettings) =>
    Object.keys(formPaperlessSettings).map((hashedLoanNumber) => {
      const { header, loanNumber } = loanDataDict[hashedLoanNumber];
      const addressData = [];
      if (header) {
        const {
          address1,
          city,
          postalCode,
          stateCode,
        } = header;
        addressData.push(
          capitalizeString(address1),
          `${capitalizeString(city)}, ${stateCode} ${postalCode}`,
        );
      }

      const {
        isButtonDisabled,
        isToggleDisabled,
        isToggleOn,
        toggleText,
      } = getPaperlessFormProps(
        currentPaperlessSettings[hashedLoanNumber],
        formPaperlessSettings[hashedLoanNumber],
      );

      return {
        addressData,
        hashedLoanNumber,
        isButtonDisabled,
        isToggleDisabled,
        isToggleOn,
        loanNumber,
        toggleText,
      };
    }),
);

const selectReceivesPaymentEmails = () => createSelector(
  makeSelectCommunications('alertsProfile'),
  selectAutopayForm('alerts'),
  selectCurrentPaymentAlertSetting(),
  ({ isEmailAvailable }, { receivesPaymentEmails }, { isEmailAllowed }) => {
    if (isEmailAvailable && isEmailAllowed) {
      return receivesPaymentEmails;
    }
    return null;
  },
);

const selectRequestStatus = (prop) => createSelector(
  makeSelectCommunications('requestStatus'),
  (requestStatus) => requestStatus[prop],
);

const selectSelectedLoan = () => createSelector(
  getLoanDataDictionary(),
  makeSelectCommunications('selectedLoan'),
  (loanDataDict, selectedLoan) => selectedLoan in loanDataDict
    ? loanDataDict[selectedLoan].loanNumber
    : '',
);

const selectSettingsPaperlessButtonDetails = () => createSelector(
  makeSelectCommunications('currentPaperlessSettings'),
  selectIntl,
  (currentPaperlessSettings, { formatMessage }) => {
    if (!isEmpty(currentPaperlessSettings)) {
      const currentSettingValues = Object.values(currentPaperlessSettings);
      if (currentSettingValues.length === 1) {
        const { status } = currentSettingValues[0];
        return PAPERLESS_TOGGLE_TEXT_DICT[status];
      }
      return getMultiLoanPaperlessDetails(currentSettingValues, formatMessage);
    }
    return '';
  },
);

const selectUpdateAlertSettingsPayload = () => createSelector(
  makeSelectCommunications('currentAlertSettings'),
  selectAutopayForm('alerts'),
  (currentAlertSettings, { receivesPaymentEmails }) => {
    const newSetting = currentAlertSettings.map(
      (obj) => obj.alertId === 4 ? { ...obj, isEmailEnabled: receivesPaymentEmails } : obj,
    );
    return { newSetting };
  },
);

/**
 * Default selector used by Communications
 */
const makeSelectCommunications = (prop) => createSelector(
  selectCommunicationsDomain,
  (communicationsState) => communicationsState[prop],
);

export default makeSelectCommunications;
export {
  selectCommunicationsDomain,
  selectCommunicationsScreenReaderNotifications,
  selectCurrentPaymentAlertSetting,
  selectDesktopSettingsRowsData,
  selectError,
  selectFetchErrorCopy,
  selectFormAlertSettings,
  selectFormPaperlessSettings,
  selectHasMobileNumberOnFile,
  selectIsEligibleForPaperlessBilling,
  selectNotificationsFormProps,
  selectPaperlessFormData,
  selectReceivesPaymentEmails,
  selectRequestStatus,
  selectSelectedLoan,
  selectSettingsPaperlessButtonDetails,
  selectUpdateAlertSettingsPayload,
};
